/**
 * Client-seitige Filterfunktionen fuer Transactions/Subscriptions.
 * Werden auf die bereits von der API geladenen Daten angewendet.
 */

/**
 * Filtert nach Datumsbereich (inklusiv).
 * from: >= Tagesbeginn 00:00:00
 * to: <= Tagesende 23:59:59.999
 * Items ohne gueltigen Date-Wert werden ausgefiltert wenn ein Range gesetzt ist.
 */
export function filterByDateRange<T>(
  items: readonly T[],
  dateKey: keyof T,
  from?: Date,
  to?: Date,
): T[] {
  if (!from && !to) return [...items];

  const fromTime = from ? startOfDay(from).getTime() : -Infinity;
  const toTime = to ? endOfDay(to).getTime() : Infinity;

  return items.filter((item) => {
    const raw = item[dateKey];
    if (raw == null) return false;

    const time = new Date(raw as string).getTime();
    if (isNaN(time)) return false;

    return time >= fromTime && time <= toTime;
  });
}

/**
 * Filtert nach Betragsbereich. min/max in Cents.
 * Items ohne Betrag werden ausgefiltert wenn ein Range gesetzt ist.
 */
export function filterByAmountRange<T>(
  items: readonly T[],
  amountKey: keyof T,
  min?: number,
  max?: number,
): T[] {
  if (min == null && max == null) return [...items];

  return items.filter((item) => {
    const amount = item[amountKey];
    if (amount == null || typeof amount !== 'number') return false;

    if (min != null && amount < min) return false;
    if (max != null && amount > max) return false;
    return true;
  });
}

/**
 * Filtert nach Textfeld (case-insensitiv, Teilstring-Match).
 * Items ohne Wert im Feld werden ausgefiltert wenn ein Suchtext gesetzt ist.
 */
export function filterByText<T>(
  items: readonly T[],
  key: keyof T,
  value?: string,
): T[] {
  if (!value || value.trim() === '') return [...items];

  const search = value.trim().toLowerCase();

  return items.filter((item) => {
    const fieldValue = item[key];
    if (fieldValue == null) return false;
    return String(fieldValue).toLowerCase().includes(search);
  });
}

/**
 * Konvertiert EUR-Eingabe (string) in Cents (number).
 * Nutzt Math.round um Floating-Point-Fehler zu vermeiden.
 * Gibt undefined zurueck bei leerem/ungueltigem Input.
 */
export function euroToCents(value: string | undefined): number | undefined {
  if (!value || value.trim() === '') return undefined;
  const parsed = parseFloat(value);
  if (isNaN(parsed) || parsed < 0) return undefined;
  return Math.round(parsed * 100);
}

// --- Hilfsfunktionen ---

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}
